import { useEffect, useState } from "react";
import AchievementTile from "./AchievementTile";
import i18n from "../../../language/i18n";
import {
  PrimaryButton,
  retrieveAchievementRuleListByParams,
  retrieveAchievementRuleTextByParams,
} from "../../../library";
import { newEarnedAchievementCelebrationStyles } from "./NewEarnedAchievementCelebrationStyles";

interface NewEarnedAchievementCelebrationProps {
  earnedAchievementDTOList: any[];
  onClose: () => void;
}

// Confetti palette + a fixed piece list. Trajectories are index-derived (no runtime randomness) so
// the burst is deterministic; the pieces are re-mounted (keyed on the active achievement) to replay it.
const CONFETTI_COLORS = [
  "var(--catchup-blue)",
  "var(--catchup-green)",
  "var(--catchup-orange)",
  "var(--accent-gold)",
  "var(--catchup-purple)",
  "var(--brand-light)",
];
const CONFETTI_PIECES = Array.from({ length: 30 }, (_, i) => i);

const coterieLabel = (coterieType: string) =>
  coterieType === "MANAGEMENT" ? i18n.t("total") : i18n.t(coterieType);

// The celebratory reveal for freshly-earned achievement(s), rendered inside the App-level BaseModal.
// One achievement → a single hero + tier ladder; several → a paged carousel (‹ dots N/total, CTA advances).
const NewEarnedAchievementCelebration = (props: NewEarnedAchievementCelebrationProps) => {
  const { earnedAchievementDTOList, onClose } = props;
  const [index, setIndex] = useState(0);
  const total = earnedAchievementDTOList.length;

  // Achievements keep arriving over the websocket while the modal is open; if the list shrinks/grows,
  // keep the pointer in range rather than reading past the end.
  const safeIndex = Math.min(index, Math.max(0, total - 1));
  useEffect(() => {
    if (index > total - 1) setIndex(Math.max(0, total - 1));
  }, [total, index]);

  const current = earnedAchievementDTOList[safeIndex];
  if (!current) return null;

  const { achievementDTO, level } = current;
  const { coterieType, sourceType, trackingType } = achievementDTO;
  const ruleList = retrieveAchievementRuleListByParams(
    coterieType,
    sourceType,
    trackingType,
  );
  const ruleText = retrieveAchievementRuleTextByParams(
    coterieType,
    sourceType,
    trackingType,
    level,
  );
  const isMulti = total > 1;
  const isLast = safeIndex >= total - 1;

  return (
    <div className="nbc">
      <style>{newEarnedAchievementCelebrationStyles}</style>

      <div className="nbc-confetti" key={`confetti-${safeIndex}`} aria-hidden="true">
        {CONFETTI_PIECES.map((i) => {
          const isStrip = i % 3 === 0;
          return (
            <span
              key={i}
              className="nbc-confetti__piece"
              style={
                {
                  left: `${(i * 37) % 100}%`,
                  background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
                  animationDelay: `${(i % 12) * 0.06}s`,
                  animationDuration: `${1.7 + (i % 5) * 0.18}s`,
                  width: isStrip ? "7px" : "9px",
                  height: isStrip ? "13px" : "9px",
                  borderRadius: isStrip ? "2px" : "50%",
                  "--nbc-dx": `${((i % 7) - 3) * 26}px`,
                  "--nbc-rot": `${(i % 2 ? 1 : -1) * (220 + (i % 5) * 90)}deg`,
                } as any
              }
            />
          );
        })}
      </div>

      <div className="nbc-stage" key={`stage-${safeIndex}`}>
        <div className="nbc-hero">
          <span className="nbc-hero__glow" aria-hidden="true" />
          <span className="nbc-hero__ring" aria-hidden="true" />
          <span className="nbc-hero__comet" aria-hidden="true" />
          <div className="nbc-hero__tile">
            <AchievementTile earnedAchievementDTO={current} size="7xlarge" grayscale={false} />
            <span className="nbc-hero__shine" aria-hidden="true" />
          </div>
        </div>

        <div className="nbc-info">
          <div className="nbc-chips">
            <span className="nbc-chip nbc-chip--subject">
              {coterieLabel(coterieType)}
            </span>
            <span className="nbc-chip nbc-chip--level">
              {i18n.t("level")} {level}
            </span>
          </div>
          {ruleText ? <p className="nbc-rule">{ruleText}</p> : null}

          {ruleList.length > 0 ? (
            <div className="nbc-ladder">
              <div className="nbc-ladder__track">
                <span
                  style={{
                    width: `${Math.max(0, Math.min(100, ((level - 1) / 4) * 100))}%`,
                  }}
                />
              </div>
              <div className="nbc-ladder__tiles">
                {[1, 2, 3, 4, 5].map((tier) => {
                  const earned = tier <= level;
                  const isNew = tier === level;
                  return (
                    <div
                      key={tier}
                      className={`nbc-tier ${isNew ? "is-new" : ""} ${
                        earned ? "is-earned" : "is-locked"
                      }`}
                    >
                      <AchievementTile
                        earnedAchievementDTO={{ achievementDTO, level: tier }}
                        size="3xlarge"
                        grayscale={!earned}
                      />
                      {isNew ? (
                        <span className="nbc-tier__tag">
                          {i18n.t("new_achievement_tag")}
                        </span>
                      ) : earned ? (
                        <span className="nbc-tier__tick">✓</span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="nbc-foot">
        {isMulti ? (
          <>
            <button
              type="button"
              className="nbc-nav"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={safeIndex === 0}
              aria-label={i18n.t("previous")}
            >
              ‹
            </button>
            <div className="nbc-dots" aria-hidden="true">
              {earnedAchievementDTOList.map((_, i) => (
                <span
                  key={i}
                  className={`nbc-dot ${i === safeIndex ? "is-on" : ""}`}
                />
              ))}
            </div>
            <span className="nbc-count">
              {safeIndex + 1} / {total}
            </span>
            {isLast ? (
              <PrimaryButton
                title={`${i18n.t("awesome")} 🎉`}
                size="large"
                onClick={onClose}
              />
            ) : (
              <PrimaryButton
                title={i18n.t("next")}
                size="large"
                iconPosition="right"
                onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
              />
            )}
          </>
        ) : (
          <PrimaryButton
            title={`${i18n.t("awesome")} 🎉`}
            size="large"
            onClick={onClose}
          />
        )}
      </div>
    </div>
  );
};

export default NewEarnedAchievementCelebration;
